import { inject, Injectable } from '@angular/core';
import { CONFIG_TOKEN } from '@pxl/models/common';
import { LoggingService } from '@pxl/models/contracts';
import * as Sentry from '@sentry/angular';
import { SeverityLevel } from '@sentry/angular';

const emptyLogFunction = (...data: any[]) => {};

const makeSentryLog = (level: 'fatal' | 'error' | 'warning' | 'info' | 'debug', consoleFn: (...data: any[]) => void) => {
    return (...data: any[]) => {
        if (level === 'fatal' || level === 'error') {
            Sentry.addBreadcrumb({
                category: 'loggingService',
                message: `${data[0]}`,
                data: { data },
                timestamp: Date.now() / 1000,
            });
        }
        consoleFn(...data);
    };
};

const makeSentryTimeStart = (doOutput: boolean) => (message: string) => {
    Sentry.addBreadcrumb({
        category: 'timeStart',
        message: message,
        timestamp: Date.now() / 1000,
    });
    if (doOutput) {
        // eslint-disable-next-line no-console
        console.time(message);
    }
};

const makeSentryTimeEnd = (doOutput: boolean) => (message: string) => {
    Sentry.addBreadcrumb({
        category: 'timeEnd',
        message: message,
        timestamp: Date.now() / 1000,
    });
    if (doOutput) {
        // eslint-disable-next-line no-console
        console.timeEnd(message);
    }
};

@Injectable({ providedIn: 'root' })
export class DefaultLoggingService implements LoggingService {
    private readonly config = inject(CONFIG_TOKEN);

    public message(message: string, level: SeverityLevel, contexts: Record<string, any>): void {
        this.logObj.error(message, contexts);
        if (this.config.sentryDsn) {
            Sentry.captureMessage(message, {
                level,
                contexts,
            });
        }
    }

    public get debug() {
        return this.logObj.debug;
    }

    public get info() {
        return this.logObj.info;
    }

    public get warn() {
        return this.logObj.warn;
    }

    public get error() {
        return this.logObj.error;
    }

    public get trace() {
        return this.logObj.trace;
    }

    public get time() {
        return this.logObj.time;
    }

    public get timeEnd() {
        return this.logObj.timeEnd;
    }

    public async promiseTime<T>(message: string, promise: Promise<T>): Promise<T> {
        this.time(message);
        const value = await promise;
        this.timeEnd(message);
        return value;
    }

    private get logObj() {
        const doOutput = this.config.IS_ENVIRONMENT_DEVELOPMENT || (window as any)['pxl_logging'];
        return {
            // eslint-disable-next-line no-console
            debug: makeSentryLog('debug', doOutput ? console.debug : emptyLogFunction),
            // eslint-disable-next-line no-console
            info: makeSentryLog('info', doOutput ? console.info : emptyLogFunction),
            // eslint-disable-next-line no-console
            warn: makeSentryLog('warning', doOutput ? console.warn : emptyLogFunction),
            // eslint-disable-next-line no-console
            error: makeSentryLog('error', doOutput ? console.error : emptyLogFunction),
            // eslint-disable-next-line no-console
            trace: makeSentryLog('info', doOutput ? console.trace : emptyLogFunction),
            time: makeSentryTimeStart(doOutput),
            timeEnd: makeSentryTimeEnd(doOutput),
        };
    }
}
